import { Modal } from '@mui/material';
import classNames from 'classnames';
import React, { memo, PropsWithChildren } from 'react';
import { useLocalization } from '../../Contexts/Localization';
import { GMIconButton } from '../Buttons/IconButton';
import Printer from '../../Assets/Icons/print.svg?react';
import { ZoomInMap, ZoomOutMap, Close } from '@mui/icons-material';
import css from './Modal.module.scss';
import { ArrowBack } from '@mui/icons-material';
import { ErrorText } from '../Text/Text';

export interface ModalProps {
    /**
     * Whether the modal is open
     */
    open: boolean;
    /**
     * Callback for when the backdrop is clicked
     */
    onClose?: () => void;
    /**
     * Whether a darker backdrop shown be shown
     */
    darkBackdrop?: boolean;
    /**
     * Elements to display in the modal
     */
    children: any;
    /**
     * Where to position the modal. Default 'center'
     */
    position?: 'top' | 'center';
    /**
     * Optional styles to apply
     */
    className?: string;
    /**
     * Makes the modal take the full screen
     */
    fullScreen?: boolean;
    /**
     * When true, we disable the backdrop click and other keys from causing the modal to close.
     */
    disableCloseModal?: boolean;
}

export const GMModal: React.FC<ModalProps> = memo(({ position = 'center', ...props }) => {
    return (
        <Modal
            className={props.darkBackdrop ? css.darkBackdrop : ''}
            open={props.open}
            // TODO: Investigate onClose since this is deprecated
            onBackdropClick={() => {
                if (props.onClose && !props.disableCloseModal) {
                    props.onClose();
                }
            }}
        >
            <div
                className={classNames(
                    css.modal,
                    css.background,
                    position === 'center' ? css.center : css.top,
                    props.className,
                    {
                        [css.fullScreen]: !!props.fullScreen,
                    },
                )}
            >
                {props.children}
            </div>
        </Modal>
    );
});

interface ModalHeaderProps extends PropsWithChildren {
    /**
     * Callback for when the close icon is clicked. If undefined no icon will
     * be shown
     */
    onClose?: () => void;
    /**
     * Callback when the expand/shrink icon is clicked. If undefined, no icon will be shown.
     */
    onToggleExpand?: () => void;
    /**
     * Flag for when the modal is expanded.
     */
    expanded?: boolean;
    /**
     * Callback for when the print icon is clicked. If undefined, no icon will be
     * shown. If null, a disabled icon will be shown.
     */
    onPrint?: (() => void) | null;
    /**
     * Where to display the X button if onClose isn't undefined, default right
     */
    closeButtonPosition?: 'right' | 'left';
    /**
     * Callback for when the back icon is clicked. If undefined no icon will
     * be shown. If defined a back button is added to the lefthand side of
     * the header
     */
    onBack?: () => void;
    /**
     * Whether the title should be centered
     */
    centerTitle?: boolean;
    /**
     * Disable the Close button in the header when true.
     */
    disableCloseButton?: boolean;
}

export const GMModalHeader: React.FC<ModalHeaderProps> = memo(({ closeButtonPosition = 'right', ...props }) => {
    const Localization = useLocalization();

    return (
        <div className={classNames(css.header, {
            [css.hasBtnRight]: props.onClose && closeButtonPosition === 'right',
            [css.hasBtnLeft]: !!props.onBack || (props.onClose && closeButtonPosition === 'left'),
        })}
        >
            {props.onClose && closeButtonPosition === 'left' ?
                <div>
                    <GMIconButton
                        disableRipple
                        color={'light'}
                        onClick={props.onClose}
                        tooltip={Localization.Close}
                        disabled={props.disableCloseButton}
                    >
                        <Close />
                    </GMIconButton>
                </div> : null
            }
            {props.onBack ?
                <div>
                    <GMIconButton
                        disableRipple
                        color={'light'}
                        onClick={props.onBack}
                        tooltip={Localization.Back}
                    >
                        <ArrowBack />
                    </GMIconButton>
                </div> : null
            }
            <div className={classNames(css.title, {
                [css.centerTitle]: props.centerTitle,
                [css.hasLeftIcon]: !!props.onBack || (props.onClose && closeButtonPosition === 'left'),
            })}
            >
                {props.children}
            </div>
            {props.onPrint !== undefined ?
                <div>
                    <GMIconButton
                        disableRipple
                        color={'light'}
                        onClick={props.onPrint ?? (() => null)}
                        disabled={props.onPrint === null}
                        tooltip={Localization.Print}
                    >
                        <Printer className={css.printIcon} />
                    </GMIconButton>
                </div> : null
            }
            {props.onToggleExpand ?
                <div>
                    <GMIconButton
                        disableRipple
                        color={'light'}
                        onClick={props.onToggleExpand}
                        tooltip={props.expanded ? Localization.Shrink : Localization.Expand}
                    >
                        {props.expanded ? <ZoomInMap /> : <ZoomOutMap />}
                    </GMIconButton>
                </div> : null
            }
            {props.onClose && closeButtonPosition !== 'left' ?
                <div>
                    <GMIconButton
                        disableRipple
                        color={'light'}
                        onClick={props.onClose}
                        tooltip={Localization.Close}
                        disabled={props.disableCloseButton}
                    >
                        <Close />
                    </GMIconButton>
                </div> : null
            }
        </div>
    );
});

interface ModalBodyProps extends PropsWithChildren {
    /**
     * Class name to apply to the modal body
     */
    className?: string;
    /**
     * Removes outer padding
     */
    noPadding?: boolean;
}

export const GMModalBody: React.FC<ModalBodyProps> = memo((props) => {
    return (
        <div className={classNames(css.body, props.className, { [css.noPadding]: props.noPadding })}>
            {props.children}
        </div>
    );
});

export const GMModalFooter: React.FC<PropsWithChildren> = memo((props) => {
    return (
        <div className={css.footer}>
            {props.children}
        </div>
    );
});

interface ModalErrorProps {
    /*
     * Inner contents of the error modal
     */
    children: any;
}

export const GMModalError: React.FC<ModalErrorProps> = memo((props) => {
    return (
        <div className={css.error}>
            <ErrorText>
                {props.children}
            </ErrorText>
        </div>
    );
});